import { useEffect, useRef, useState } from 'react';
import {
	Dialog,
	DialogContent,
	Grid,
	Typography,
	IconButton as MaterialIconButton,
	Box,
	Button,
	LinearProgress,
} from '@mui/material';
import {
	BotHomeIcon,
	ClipBoardIcon,
	CloseIcon,
	LeftArrowIcon,
	MapIcon,
	RightArrowIcon,
	StarIcon,
	TickIcon,
} from '../assets/icons/Icons';
import IconButton from './IconButton';
import UserGuideImg from '../assets/images/Guide.png';
import UserGuideIntro from '../assets/images/UserGuideIntro.png';
import userGuideImages from './UserGuideImages';
import { getEnabledUserFeatures } from '../context/MainProvider';
import { FEATURE_TYPES } from '../Utils/Enums';

const residentAddingInstruction = [
	{
		instruction01:
			"Enter the resident's ID and name in the provided fields.",
		instruction02:
			"Fill in the resident's unique ID and full name to begin.",
		image: UserGuideImg,
	},
	{
		instruction01: 'Upload profile file or medical history',
		instruction02:
			"Click 'Upload' to attach the resident's profile and any medical history reports in .text or .pdf format.",
		image: UserGuideImg,
	},
];

const medicalBotInstruction = [
	{
		instruction01: 'Open the Medical Bot.',
		instruction02:
			"Click on the 'Medical Bot' icon to open the chat window.",
		image: userGuideImages.MedicalBot1,
	},
	{
		instruction01: 'Ask a question',
		instruction02:
			'Type any elder care-related query to get instant assistance.',
		image: userGuideImages.MedicalBot1,
	},
];

const initialCarePlanInstruction = [
	{
		instruction01: 'Create a resident ID .',
		instruction02: 'Create a new one.',
		image: userGuideImages.InitialPlan01,
	},
	{
		instruction01: 'Upload necessary documents',
		instruction02:
			"Upload the resident's profile and medical reports in PDF file.",
		image: userGuideImages.InitialPlan02,
	},
	{
		instruction01: 'Customize care plan details',
		instruction02: 'Click continue to get guidance to customize the plan.',
		image: userGuideImages.InitialPlan03,
	},
	{
		instruction01: 'Review the medical conditions',
		instruction02:
			"A pop-up will guide you to choose relevant medical conditions to be considered in the care plan as per uploaded resident's file.",
		image: userGuideImages.InitialPlan04,
	},
	{
		instruction01: 'Review the care categories.',
		instruction02:
			'A pop-up will guide you to choose required care categories to be considered in the care plan',
		image: userGuideImages.InitialPlan05,
	},
	{
		instruction01: 'Generate the plan.',
		instruction02: "Click 'Create Plan' to generate the care plan",
		image: userGuideImages.InitialPlan06,
	},
	{
		instruction01: 'Review and download options.',
		instruction02:
			"Review the plan, edit sub-categories or download it as a PDF and generate new plan by clicking 'Create New Plan'",
		image: userGuideImages.InitialPlan06,
	},
];

const carePlanInstruction = [
	{
		instruction01: 'Upload the current plan',
		instruction02:
			'Upload the current care plan file to start the optimization process.',
		image: UserGuideImg,
	},
	{
		instruction01: 'Review the current plan',
		instruction02:
			"The current plan will be displayed with the title 'Current Plan'.",
		image: UserGuideImg,
	},
	{
		instruction01: 'Provide instruction or optimize the whole plan',
		instruction02:
			"Mention specific instructions for subsections or click 'Optimize Whole Plan'.",
		image: UserGuideImg,
	},
	{
		instruction01: 'View the optimized plan',
		instruction02:
			"The optimized plan will appear with the title 'Optimized Plan'. Any updated categories will be flagged.",
		image: UserGuideImg,
	},
	{
		instruction01: 'Editing and download.',
		instruction02:
			'Edit the flagged categories or download the entire optimized plan as a PDF.',
		image: UserGuideImg,
	},
];

const Instruction = [initialCarePlanInstruction, medicalBotInstruction];

const MAIN_TAB_ITEMS = [
	{
		icon: ClipBoardIcon,
		text: 'Create a Care Plan',
		key: FEATURE_TYPES[1].key,
	},
	{
		icon: BotHomeIcon,
		text: 'Get Medical Advice',
		key: FEATURE_TYPES[2].key,
	},
];

const TAB_ITEMS = [
	{
		icon: MapIcon,
		text: 'Onboarding a Resident',
		key: FEATURE_TYPES[0].key,
	},
	{
		icon: StarIcon,
		text: 'Optimize a Care Plan',
		key: FEATURE_TYPES[3].key,
	},
];

function SectionTab({ text, onClick, Icon, isSectionSelected }) {
	return (
		<MaterialIconButton
			disableRipple
			onClick={() => onClick(text)}
			style={{ backgroundColor: 'transparent', padding: '0px' }}>
			<Grid
				container
				borderRadius={'5px'}
				bgcolor={'#F5F5F5'}
				justifyContent={'center'}
				alignItems={'center'}>
				<Grid
					container
					paddingY={'10px'}
					paddingX={'15px'}
					alignItems={'center'}
					flexDirection={'row'}>
					<Icon />
					<Typography
						fontSize={'13px'}
						fontWeight={'600'}
						marginLeft={'10px'}
						color={'#5D5D7F'}>
						{text}
					</Typography>
				</Grid>
				{isSectionSelected && (
					<Grid
						container
						height={'5px'}
						sx={{
							backgroundColor: '#28696D',
							borderBottomLeftRadius: '5px',
							borderBottomRightRadius: '5px',
						}}
					/>
				)}
			</Grid>
		</MaterialIconButton>
	);
}

function CustomStepper({ progressList }) {
	return (
		<Grid
			container
			flexDirection={'row'}
			justifyContent={'center'}
			paddingY={'10px'}>
			{progressList.map((progress, index) => {
				return (
					<Box key={index} sx={{ width: '10%', marginX: '5px' }}>
						<LinearProgress
							sx={{
								'& .MuiLinearProgress-bar1Determinate': {
									backgroundColor: '#28696D',
								},
								backgroundColor: '#F5F5F5',
								height: 5,
								borderRadius: '10px',
							}}
							variant="determinate"
							value={progress}
						/>
					</Box>
				);
			})}
		</Grid>
	);
}

function UserGuidePopup({ isModalOpen, onClose }) {
	const [guideTabs, setGuideTabs] = useState(MAIN_TAB_ITEMS);
	const [userInstructions, setUserInstructions] = useState(Instruction);
	const [selectedTab, setSelectedTab] = useState(MAIN_TAB_ITEMS[0].text);
	const [isStepInitiated, setIsStepInitiated] = useState(false);
	const [activeStep, setActiveStep] = useState(0);
	const [progress, setProgress] = useState([100, 0, 0, 0, 0, 0, 0]);
	const [mainSteps, setMainSteps] = useState(0);
	const [stepInstructions, setStepInstructions] = useState(Instruction[0]);
	const [isMainStepsCompleted, setIsMainStepsCompleted] = useState(false);
	const { enabledUserFeatureList } = getEnabledUserFeatures();
	const addedGuideRef = useRef(new Set()); // Using this ref to prevent showing duplicates in the guides list

	useEffect(() => {
		if (isStepInitiated) {
			onGetUserGuides();
		}
	}, [isStepInitiated]);

	function onGetUserGuides() {
		const otherFeatures = [FEATURE_TYPES[0].key, FEATURE_TYPES[3].key];
		const otherInstructions = [
			{ key: FEATURE_TYPES[0].key, data: residentAddingInstruction },
			{ key: FEATURE_TYPES[3].key, data: carePlanInstruction },
		];
		otherFeatures.map(item => {
			if (
				enabledUserFeatureList.includes(item) &&
				!addedGuideRef.current.has(item)
			) {
				const tab = TAB_ITEMS.find(value => value.key === item);
				const instruction = otherInstructions.find(
					value => value.key === item,
				);

				setGuideTabs(prevState => [...prevState, tab]);
				setUserInstructions(prevState => [
					...prevState,
					instruction.data,
				]);
				addedGuideRef.current.add(item);
			}
		});
	}

	function onChangeSection(section, selectedIndex) {
		setSelectedTab(section);
		onChangeMainStep(selectedIndex);
		setMainSteps(selectedIndex);
	}

	function handleNext(step) {
		const clone = progress;
		clone[step] = 100;
		setProgress(clone);
	}

	function handlePrevious(step) {
		const clone = progress;
		clone[step] = 0;
		setProgress(clone);
	}

	function nextStep() {
		if (isMainStepsCompleted) {
			return;
		}

		const updatedActiveStep = activeStep + 1;
		if (activeStep === userInstructions[mainSteps].length - 1) {
			const updatedMainStep = mainSteps + 1;
			if (updatedMainStep !== userInstructions.length) {
				onChangeMainStep(updatedMainStep);
			} else {
				setIsMainStepsCompleted(true);
			}

			setMainSteps(updatedMainStep);
		} else {
			setActiveStep(updatedActiveStep);
			handleNext(updatedActiveStep);
		}
	}

	function previousStep() {
		const updatedActiveStep = activeStep - 1;
		setActiveStep(updatedActiveStep);
		handlePrevious(activeStep);
	}

	function onChangeMainStep(newMainStep) {
		setActiveStep(0);
		const newProgressArr = Array(userInstructions[newMainStep].length).fill(
			0,
		);
		newProgressArr[0] = 100;
		setProgress(newProgressArr);
		setStepInstructions([...userInstructions[newMainStep]]);
		setSelectedTab(guideTabs[newMainStep].text);
	}

	function getStepCount() {
		const totalCount =
			mainSteps !== userInstructions.length
				? userInstructions[mainSteps].length
				: userInstructions[userInstructions.length - 1].length;

		return `${activeStep + 1}/${totalCount} Steps`;
	}

	function onCompleteAllSteps() {
		onClose();
		setTimeout(() => {
			setIsStepInitiated(false);
			setActiveStep(0);
			setProgress([100, 0, 0, 0, 0, 0, 0]);
			setMainSteps(0);
			setStepInstructions(userInstructions[0]);
			setIsMainStepsCompleted(false);
			setSelectedTab(MAIN_TAB_ITEMS[0].text);
		}, 500);
	}

	return (
		<Dialog
			open={isModalOpen}
			onClose={onClose}
			fullWidth
			maxWidth="md"
			scroll="body">
			<DialogContent style={{ padding: '20px 10px 20px 10px' }}>
				<Grid
					container
					padding={'10px'}
					flexDirection={'row'}
					justifyContent={
						isStepInitiated ? 'space-between' : 'flex-end'
					}
					alignItems={'center'}>
					{isStepInitiated && (
						<Typography
							color={'#444444'}
							fontSize={'24px'}
							fontWeight={'600'}>
							User Guide
						</Typography>
					)}
					<MaterialIconButton
						style={{ padding: '0' }}
						onClick={onCompleteAllSteps}>
						<CloseIcon />
					</MaterialIconButton>
				</Grid>
				{isStepInitiated ? (
					<Grid container padding={'10px'}>
						<Grid
							container
							spacing={1}
							columns={{ xs: 4, sm: 8, md: 12 }}
							alignItems={'center'}
							justifyContent={'center'}>
							{guideTabs.map((item, index) => {
								return (
									<Grid item key={index} xs="auto">
										<SectionTab
											Icon={item.icon}
											text={item.text}
											onClick={tab =>
												onChangeSection(tab, index)
											}
											isSectionSelected={
												selectedTab === item.text
											}
										/>
									</Grid>
								);
							})}
						</Grid>
						<Grid
							container
							justifyContent={'center'}
							alignItems={'center'}
							paddingY={'10px'}
							flexDirection={'row'}>
							<Typography
								color={'#292D32'}
								fontSize={'16px'}
								fontWeight={'500'}
								paddingY={'2px'}>
								{getStepCount()}
							</Typography>
							<CustomStepper progressList={progress} />
							<Typography
								width={'100%'}
								textAlign={'center'}
								color={'#292D32'}
								fontSize={'14px'}
								fontWeight={'500'}
								paddingY={'5px'}>
								{stepInstructions[activeStep].instruction01}
							</Typography>
							<Box
								component={'img'}
								src={stepInstructions[activeStep].image}
								sx={{
									padding: '2px',
									backgroundColor: '#FFFFFF',
									borderRadius: '5px',
								}}
								width={'70%'}
								height={'70%'}
							/>
							<Typography
								width={'100%'}
								color={'#28696D'}
								fontSize={'14px'}
								marginTop={'10px'}
								textAlign={'center'}
								fontWeight={'500'}>
								{stepInstructions[activeStep].instruction02}
							</Typography>
						</Grid>
					</Grid>
				) : (
					<Grid
						container
						justifyContent={'center'}
						alignItems={'center'}
						paddingY={'10px'}
						flexDirection={'column'}>
						<Box
							component={'img'}
							src={UserGuideIntro}
							sx={{
								backgroundColor: '#FFFFFF',
								borderRadius: '5px',
								marginLeft: '50px',
							}}
							width={'30%'}
							height={'30%'}
						/>
						<Typography
							color={'#28696D'}
							fontSize={'36px'}
							fontWeight={'600'}>
							Welcome to SenzCare
						</Typography>
						<Typography
							color={'#9CA3AF'}
							fontSize={'14px'}
							width={'70%'}
							textAlign={'center'}
							fontWeight={'600'}>
							SenzCare is an AI-powered care assistant designed to
							help care home staff provide personalized,
							efficient, and high-quality care for residents. It
							streamlines care planning , care optimizations and
							medical assistance, allowing caregivers to focus
							more on residents&lsquo; well-being.
						</Typography>
					</Grid>
				)}
				{isStepInitiated ? (
					<Grid container justifyContent={'center'}>
						<Grid
							container
							width={'85%'}
							justifyContent={
								activeStep !== 0 && !isMainStepsCompleted
									? 'space-between'
									: 'flex-end'
							}
							paddingY={'10px'}>
							{activeStep !== 0 && !isMainStepsCompleted && (
								<MaterialIconButton onClick={previousStep}>
									<LeftArrowIcon />
								</MaterialIconButton>
							)}
							<MaterialIconButton
								onClick={
									isMainStepsCompleted
										? onCompleteAllSteps
										: nextStep
								}>
								{isMainStepsCompleted ? (
									<TickIcon />
								) : (
									<RightArrowIcon />
								)}
							</MaterialIconButton>
						</Grid>
					</Grid>
				) : (
					<Grid
						container
						justifyContent={'center'}
						paddingTop={'10px'}
						paddingX={'20px'}>
						<IconButton
							text={'Guide Me'}
							onClick={() => setIsStepInitiated(true)}
						/>
					</Grid>
				)}
			</DialogContent>
		</Dialog>
	);
}

export default UserGuidePopup;
